import { App } from 'app/console-api';
import {
  BodyText,
  FlexColumn,
  FlexRow,
  formatDate,
  formatNumber,
} from '@make-software/cspr-design';
import React from 'react';
import styled from 'styled-components';
import { AppContactsView } from 'app/components/common/contacts/app-contacts-view';
import { generatePath, useNavigate } from 'react-router-dom';
import { APPS_EDIT_DETAILS_PATH } from 'app/route/paths';
import DetailsTableRow from 'app/components/management/common/details-table-row/details-table-row';
import DetailsNameTableData from 'app/components/management/common/details-name-table-data/details-name-table-data';
import DetailsValueTableData from 'app/components/management/common/details-value-table-data/details-value-table-data';
import { CategoryTag } from 'app/components/common/categories/category-tag';
import { BoldText, StyledStatusElement } from 'app/components/common/table/styled-elements';
import { AppType } from 'app/components/common/apps/app-type';
import { StyledCaption } from 'app/components/management/apps/create/apps-create-step-1';
import { EditButton } from 'app/components/common/common-styles';

type AppDescriptiveDataProps = {
  app: App;
};

export const StyledContentRow = styled.div(({ theme }) =>
  theme.withMedia({
    position: 'relative',
    top: '11px'
  })
);

export const AppDescriptiveData = (props: AppDescriptiveDataProps) => {
  const navigate = useNavigate();
  const edit = () => {
    navigate(generatePath(APPS_EDIT_DETAILS_PATH, { appID: props.app.id }));
  };

  return (
    <FlexColumn>
      <StyledCaption margin={'24px 0 32px 17px'}>
        <BodyText size={1} scale={'lg'}>
          Details
        </BodyText>
      </StyledCaption>
      <DetailsTableRow>
        <DetailsNameTableData>ID</DetailsNameTableData>
        <DetailsValueTableData>{props.app.id}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Type</DetailsNameTableData>
        <DetailsValueTableData>
          <AppType type={props.app.type} />
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Status</DetailsNameTableData>
        <DetailsValueTableData>
          <BoldText size={4} monotype>
            {props.app.isActive ? (
              <StyledStatusElement name={'Active'} isActive={true} />
            ) : (
              <StyledStatusElement name={'Inactive'} isActive={false} />
            )}
          </BoldText>
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Name</DetailsNameTableData>
        <DetailsValueTableData>{props.app.name}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Description</DetailsNameTableData>
        <DetailsValueTableData>{props.app.description}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Categories</DetailsNameTableData>
        <DetailsValueTableData>
          <FlexRow gap={4}>
            {props.app.categories.map((categoryName) => (
              <CategoryTag key={categoryName} name={categoryName} />
            ))}
          </FlexRow>
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>MAU</DetailsNameTableData>
        <DetailsValueTableData>{formatNumber(props.app.mau)}</DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>DAU</DetailsNameTableData>
        <DetailsValueTableData>{formatNumber(props.app.dau)}</DetailsValueTableData>
      </DetailsTableRow>
      {props.app.contacts && props.app.contacts.length > 0 && (
        <DetailsTableRow>
          <DetailsNameTableData>Contacts</DetailsNameTableData>
          <DetailsValueTableData>
            <AppContactsView contacts={props.app.contacts} showAccessBadge />
          </DetailsValueTableData>
        </DetailsTableRow>
      )}
      <DetailsTableRow>
        <DetailsNameTableData>Created At</DetailsNameTableData>
        <DetailsValueTableData>
          {formatDate(props.app.createdAt.toLocaleString())}
        </DetailsValueTableData>
      </DetailsTableRow>
      <DetailsTableRow>
        <DetailsNameTableData>Updated At</DetailsNameTableData>
        <DetailsValueTableData>
          {formatDate(props.app.createdAt.toLocaleString())}
        </DetailsValueTableData>
      </DetailsTableRow>
      <EditButton onClick={edit}>Edit</EditButton>
    </FlexColumn>
  );
};
